import { useState } from 'react';
import { useLocation } from 'react-router-dom';

import endpoints from '../../../../../../api/endpoints';
import { useFetch } from '../../../../../../api/useFetch';
import CommonReports from '../../../../../common/reports';
import { downloadReport } from '../../../../../common/reports/utils/downloadReport';
import { HandleDownloadReport } from '../../../../../common/reports/actions/interfaces';
import { getPermissionToGenerateReports } from '../../../../../common/reports/utils/mapReportToTableStructure';
import { addParams, fetchWithTokenRefresh } from '../../../../../../api/fetchWithTokenRefresh';
import { FiltersFormValues, FiltersInitialData, ReportsData } from '../../../../../common/reports/interfaces';

import locales from './locales';
import FiltersForm from './filters/filtersForm';
import { mapTableDataIntoApiFormat } from './utils';
import FilterDetails from './filters/filterDetails';
import FiltersSummary from './filters/filtersSummary';
import { VoiceReportCreateForm, VoiceReportDetailsMetaDataResponse } from './interfaces';
import { filtersValidationSchema } from './filters/filtersValidation';

const initFilters = {
  name: '',
  operators: [],
  operationIds: [],
  msisdns: [],
  voiceAppIds: [],
  sessionTags: [],
  sessionCloseReasons: [],
  from: null,
  to: null
};

const Reports = () => {
  const { data = {} as ReportsData, loading, refresh, error } = useFetch<ReportsData>({
    pathname: endpoints.voiceReports
  });

  const handleDeleteReportRequest = (id: string) =>
    fetchWithTokenRefresh(`${endpoints.voiceReports}/${id}`, {
      method: 'delete'
    });

  const handleCancelReportRequest = (id: string) =>
    fetchWithTokenRefresh(`${endpoints.voiceReports}/${id}`, {
      method: 'delete'
    });

  const handleRetryReportRequest = (id: string) =>
    fetchWithTokenRefresh(`${endpoints.voiceReportsRetry(id)}`, {
      method: 'POST'
    });

  const handleRegenerateReportRequest = (id: string) =>
    fetchWithTokenRefresh(`${endpoints.voiceReportsRegenerate(id)}`, {
      method: 'POST'
    });

  const handleGenerateReportRequest = (values: VoiceReportCreateForm) =>
    fetchWithTokenRefresh(endpoints.voiceReports, addParams('POST', mapTableDataIntoApiFormat(values)));

  const handleDownloadReport: HandleDownloadReport = (id, name, size) => {
    downloadReport(id, `${name}.csv`, 'voice', size);
  };

  const location = useLocation<{ filters: FiltersInitialData } | undefined>();

  const [initialValues, setInitialValues] = useState(location.state?.filters || initFilters);

  return (
    <CommonReports
      error={error}
      filters={{
        form: (filtersFormValues: FiltersFormValues<VoiceReportCreateForm>) => <FiltersForm {...filtersFormValues} />,
        validation: filtersValidationSchema,
        details: (metadata: VoiceReportDetailsMetaDataResponse) => <FilterDetails filters={metadata} />,
        summary: (metadata: VoiceReportDetailsMetaDataResponse) => <FiltersSummary filters={metadata} />,
        initialValues,
        clearValues: () => {
          setInitialValues(initFilters);
        },
        predefinedReportGeneration: Boolean(location.state?.filters)
      }}
      locales={locales}
      data={data}
      emptyMsg="You don't have any Voice reports to display"
      loading={loading}
      refresh={refresh}
      handleDeleteReportRequest={handleDeleteReportRequest}
      handleCancelReportRequest={handleCancelReportRequest}
      handleRetryReportRequest={handleRetryReportRequest}
      handleRegenerateReportRequest={handleRegenerateReportRequest}
      handleGenerateReportRequest={handleGenerateReportRequest}
      generationAllowed={getPermissionToGenerateReports(data.limits?.status)}
      handleDownloadReport={handleDownloadReport}
    />
  );
};

export default Reports;
